import { DEFAULT_DOWN_PAYMENT_PERCENT } from '@atc/wallet-payment-fns';
import { addNewRelicPageAction } from '@ddc/new-relic';
import {
	DDC_DEALER_DR_LEASE_ENABLED,
	WALLET_UPDATE_STATE,
	WALLET_CONFIG_KEY,
	WALLET_DEFAULT_OBJ,
	SECTIONS_VDP_PAGE_ID_PARTIAL,
	UDS_THEME,
} from '../global-constants.js';
import { ddcLog } from '../utils/logger.js';
import getDRAdminConfig from '../integrations/getDRAdminConfig.js';

// TODO Make a way in the API to append CSS (or other things) to the head?
// Or load this as an external file? (Not great for performance, though.)
export const appendToHead = (markup) => {
	document.querySelector('head').appendChild(markup);
}

export const capitalizeFirstLetter = string => {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

// 'User Geographic Routing' 😆
export const getRegion = (userRegion = '') => {
	const westStates = ['AZ', 'CA', 'CO', 'ID', 'KS', 'MT', 'ND', 'NE', 'NM', 'NV', 'OR', 'SD', 'UT', 'WA', 'WY'];
	return westStates.includes(userRegion) ? 'us-west-2' : 'us-east-1';
}

export const getPrivateAPIContent = (category, field, defaultValue) => {
	return window?.DDC?.PrivateAPI?.content[category]?.[field] || defaultValue;
}

export const stringToNumber = (str) => {
	if (str === null || str === undefined) {
		return null;
	}
	if (typeof str === 'number') {
		return str;
	}
	return parseInt(str.replace(/[^0-9.-]+/g, ''), 10);
};

export const getTheme = (pageData) => {
	const themeKit = pageData?.design?.themekit;
	
	if (!themeKit) {
		return undefined;
	}

	if (themeKit.endsWith('BLACK')) {
		return UDS_THEME.DARK;
	}

	if (themeKit.endsWith('WHITE')) {
		return UDS_THEME.LIGHT;
	}

	return undefined;
};

const getWalletConfigLs = () => JSON.parse(localStorage.getItem(WALLET_CONFIG_KEY)) || WALLET_DEFAULT_OBJ;

const getWalletDataIsDefault = walletData => JSON.stringify(walletData) === JSON.stringify(WALLET_DEFAULT_OBJ);

const tryWalletConfig = () => {
	return new Promise(resolve => {
		let walletDataReceived = false;

		const getPaymentsInfoCompletedHandler = (walletData) => {
			walletDataReceived = true;
			resolve(walletData);
		};
		// subscribe to completed event to get wallet data
		window.caiEventBus.on('wallet:getPaymentsInformation:completed', getPaymentsInfoCompletedHandler);

		// emit get payment info request to get wallet data
		window.caiEventBus.emit('wallet:getPaymentsInformation:request');

		setTimeout(() => {
			// unlisten/unregister get payment info completed event to avoid duplicates handler with wallet interactions/filter changes
			window.caiEventBus.off('wallet:getPaymentsInformation:completed', getPaymentsInfoCompletedHandler);
			if (!walletDataReceived) {
				addNewRelicPageAction('ddc-mywallet-integration wallet data not resolved in time');
				resolve(null);
			}
		}, 500);
	});
}

export const getWalletConfig = () => {
	// eslint-disable-next-line
	return new Promise(async resolve => {
		let walletData = await tryWalletConfig();

		// If we don't get walletData or it's just the default object with null/empty values
		// try to get wallet from local storage
		if (getWalletDataIsDefault(walletData) || walletData === null) {
			walletData = getWalletConfigLs();
			addNewRelicPageAction('ddc-mywallet-integration wallet data is default or null', {
				walletData
			});
		}
		resolve(walletData);
	});
}

export const getWalletData = async (field1, field2) => {
	const config = await getWalletConfig();

	if (!field1) {
		return null;
	}

	if (field2) {
		return config[field1][field2];
	}

	return config[field1];
}

export const stringToElements = (inputHtml) => {
	const result = document.createElement('template');
	const html = inputHtml.trim();
	result.innerHTML = html;
	return result.content.firstChild;
}

// Get MMD ID from dealer-level data only (does NOT look at vehicle dealer codes)
// Used for initial DR Admin config fetch before checking locationToSendLeads
export const getDealerMmdId = (dealerData) => {
	return parseInt(dealerData?.dealershipCodes?.mmd, 10) ||
		parseInt(getPrivateAPIContent('digital-retailing', 'dealerId'), 10) ||
		null;
}

// Get MMD ID from vehicle's dealer codes first, then fallback to dealer data
// Used when locationToSendLeads is "vehicleOwner"
export const getVehicleMmdId = (vehicle, dealerData) => {
	return parseInt(vehicle?.dealerCodes?.mmd, 10) ||
		parseInt(dealerData?.dealershipCodes?.mmd, 10) ||
		parseInt(getPrivateAPIContent('digital-retailing', 'dealerId'), 10) ||
		null;
}

// In priority, get MMD ID from:
// - vehicle's dealer codes if it exists
// - website's dealer codes
// - Private API <-- comes from TPS integration
//   (^ will remove once mmd is in DNA for all AMD integrations)

const getMmdIdByKey = (vehicle, dealerData, key) => {
    return parseInt(vehicle?.dealerCodes?.[key], 10) ||
        parseInt(dealerData?.dealershipCodes?.[key], 10) ||
        parseInt(getPrivateAPIContent('digital-retailing', 'dealerId'), 10) ||
        null;
}

export const getMmdId = (vehicle, dealerData) => {
    return getMmdIdByKey(vehicle, dealerData, 'mmd');
}

export const getMmdIdBOA = (vehicle, dealerData) => {
    return getMmdIdByKey(vehicle, dealerData, 'mmd_boa');
}

export const setWalletUpdateState = source => {
	localStorage.setItem(WALLET_UPDATE_STATE, JSON.stringify({ lastUpdateSource: source }));
}

export const getWalletUpdateState = () => JSON.parse(localStorage.getItem(WALLET_UPDATE_STATE));

export const setDealerDRLeaseEnabled = (flag) => {
	localStorage.setItem(DDC_DEALER_DR_LEASE_ENABLED, flag);
}

export const getDealerDRLeaseEnabled = () => localStorage.getItem(DDC_DEALER_DR_LEASE_ENABLED);

export const getWalletScriptHost = (subdomain) => {
	if (window.location.search.includes('_useUdsDevEnv')) {
		const prNum = window.location.search.match(/_useUdsDevEnv=(\d+)/)[1];
		ddcLog('Using UDS dev env', { prNum });
		return `https://unified-wallet.s3.amazonaws.com/pr-${prNum}`;
	}

	return `https://${subdomain}.kbb.com/my-wallet`;
}

export const isSectionsVDP = () => window?.DDC?.dataLayer?.page?.pageInfo?.pageId?.includes(SECTIONS_VDP_PAGE_ID_PARTIAL);

export const getTenant = (config) => {
    const { sponsor = '', enableEsntialUds = false } = config;
    let tenant = 'Accelerate';

    if(sponsor === 'boa'){
        tenant = 'BOA';
    } else if(enableEsntialUds){
        tenant = 'CoxAuto';
    }

    return tenant;
};

export const getDRAdminConfigData = async (dealerData, vehicles = [], config = {}) => {
	if (!vehicles.length) return {};

	const vehicle = vehicles[0];
	const type = vehicle?.inventoryType?.toLowerCase() === 'new' ? 'New' : 'Used';

	// Check if BOA sponsor - uses different dealer code attribute
	const sponsor = config?.sponsor || '';
	const isBOASponsor = sponsor === 'boa';
	const configuredDealerCodeAttr = config?.dealerCode;
	const useStaging = config?.useStaging;

	// For BOA sponsors, use the configured dealer code attribute if available - this trumps everything
	if (isBOASponsor && configuredDealerCodeAttr) {
		const boaMmdId = parseInt(vehicle?.dealerCodes?.[configuredDealerCodeAttr], 10);
		if (boaMmdId) {
			const drAdminConfig = await getDRAdminConfig(boaMmdId, sponsor, type, useStaging);
			return { drAdminConfig, mmdId: boaMmdId, type };
		}
	}

	// Get both MMD IDs upfront
	const dealerMmdId = getDealerMmdId(dealerData);
	const vehicleMmdId = getVehicleMmdId(vehicle, dealerData);

	// If they're the same, fetch once and return
	if (dealerMmdId === vehicleMmdId) {
		const drAdminConfig = dealerMmdId ? await getDRAdminConfig(dealerMmdId, sponsor, type, useStaging) : {};
		return { drAdminConfig, mmdId: dealerMmdId, type };
	}

	// They're different - fetch dealer's DR Admin config first
	const dealerDrAdminConfig = dealerMmdId ? await getDRAdminConfig(dealerMmdId, sponsor, type, useStaging) : {};

	// Check locationToSendLeads setting based on vehicle type
	const locationSetting = type === 'New'
		? dealerDrAdminConfig?.locationToSendLeadsNew
		: dealerDrAdminConfig?.locationToSendLeadsUsed;

	// If "vehicleOwner", fetch vehicle's DR Admin config and use vehicle's MMD ID
	if (locationSetting === 'vehicleOwner') {
		const vehicleDrAdminConfig = vehicleMmdId ? await getDRAdminConfig(vehicleMmdId, sponsor, type, useStaging) : {};
		return { drAdminConfig: vehicleDrAdminConfig, mmdId: vehicleMmdId, type };
	}

	// Otherwise, use dealer's config and dealer's MMD ID
	return { drAdminConfig: dealerDrAdminConfig, mmdId: dealerMmdId, type };
};

export const ListingType = {
	New: 'New',
	Used: 'Used',
};

export const Tenant = {
	Boa: 'BOA',
	Accelerate: 'Accelerate',
};

// API.utils.getVehicleData() may omit priorityType; supplement from dataLayer.vehicles
export const getVehicle = async (API) => {
	const vehicles = await API.utils.getVehicleData();
	if (!vehicles.length) {
		return undefined;
	}
	const vehicle = vehicles[0];
	if (vehicle.priorityType !== undefined) {
		return vehicle;
	}
	const dlVehicles = window.DDC?.dataLayer?.vehicles || [];
	const dlMatch = dlVehicles.find((current) => current.uuid === vehicle.uuid || current.vin === vehicle.vin) || dlVehicles[0];
	if (dlMatch?.priorityType !== undefined) {
		return { ...vehicle, priorityType: dlMatch.priorityType };
	}
	return vehicle;
};

export const isNewListingType = (listingType) => typeof listingType === 'string' && listingType.toLowerCase() === ListingType.New.toLowerCase();

export const getPricingDetails = (pricingData) => {
	const { dPrice = [] } = pricingData;

	// Prefer semantic `typeClass`/`isFinalPrice`; `type` (MIDDLE/TOTAL) reflects stack
	// order, not identity, and can mis-map a fee as MSRP (DE753079). Fall back to type.
	// Match typeClass case-insensitively as a guard against upstream casing drift.
	const byTypeClass = (typeClass) => dPrice.find(
		(price) => price.typeClass?.toLowerCase() === typeClass.toLowerCase(),
	)?.value;

	const msrp = byTypeClass('msrp') ?? dPrice.find((price) => price.type === 'MIDDLE')?.value;
	const salePrice = dPrice.find((price) => price.isFinalPrice)?.value
		?? byTypeClass('salePrice')
		?? dPrice.find((price) => price.type === 'TOTAL')?.value;

	return {
		msrp: stringToNumber(msrp),
		salePrice: stringToNumber(salePrice),
	};
};

export const toIntOrUndef = (value) => {
	const num = parseInt(value, 10);
	return Number.isFinite(num) ? num : undefined;
};

export const numOrNull = (value) => {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};

export const parseConfigDownPaymentPercent = (config) => {
	const raw = config?.defaultDownPaymentPercent;
	if (raw === null || raw === undefined || raw === '') {
		return DEFAULT_DOWN_PAYMENT_PERCENT;
	}
	const num = parseInt(raw, 10);
	if (!Number.isFinite(num)) {
		return DEFAULT_DOWN_PAYMENT_PERCENT;
	}
	return num / 100;
};

export const resolveEcommerce = (sponsor) =>
	(sponsor === 'boa' ? Tenant.Boa : Tenant.Accelerate);

